import { useGetInstanceInfo, useGetMe } from '@postifoto/api-client';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { type AbstractIntlMessages, IntlProvider } from 'use-intl';

/**
 * Язык интерфейса сотрудников (ADR-041). Вошедший — язык из профиля; на экранах входа —
 * выбранный раньше в этом браузере, иначе язык браузера, иначе язык по умолчанию инстанса.
 * Каталоги грузятся отдельными файлами: в бандл попадает только нужный язык.
 * Даты и время — в часовом поясе инстанса, а не компьютера сотрудника.
 */
const loaders: Record<string, () => Promise<{ default: AbstractIntlMessages }>> = {
  ru: () => import('@postifoto/shared/i18n/staff/ru.json'),
  et: () => import('@postifoto/shared/i18n/staff/et.json'),
  en: () => import('@postifoto/shared/i18n/staff/en.json'),
};

const STORAGE_KEY = 'pf-staff-locale';

function readStored(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(locale: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // Приватный режим браузера: выбор действует до перезагрузки.
  }
}

interface LocaleContextValue {
  locale: string;
  locales: readonly string[];
  /** Выбор языка до входа; вошедший меняет язык в профиле (сохраняется на сервере). */
  setGuestLocale: (locale: string) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function useLocaleChoice(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocaleChoice outside StaffIntlProvider');
  return value;
}

function pick(candidates: readonly (string | null | undefined)[], supported: readonly string[]) {
  for (const candidate of candidates) {
    const language = candidate?.slice(0, 2).toLowerCase();
    if (language && supported.includes(language) && loaders[language]) return language;
  }
  return null;
}

export function StaffIntlProvider({ children }: { children: ReactNode }) {
  const instance = useGetInstanceInfo();
  const me = useGetMe({ query: { retry: false } });
  const [guest, setGuest] = useState<string | null>(readStored);
  const [catalog, setCatalog] = useState<{ locale: string; messages: AbstractIntlMessages } | null>(
    null,
  );

  const supported = instance.data?.staffLocales ?? [];
  const locale =
    pick(
      [
        me.data?.user.locale,
        guest,
        ...(navigator.languages ?? []),
        instance.data?.defaultStaffLocale,
      ],
      supported,
    ) ?? 'en';

  useEffect(() => {
    if (!instance.data || catalog?.locale === locale) return;
    let cancelled = false;
    void (loaders[locale] ?? loaders.en)?.().then((module) => {
      if (!cancelled) setCatalog({ locale, messages: module.default });
    });
    return () => {
      cancelled = true;
    };
  }, [instance.data, locale, catalog?.locale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const context = useMemo<LocaleContextValue>(
    () => ({
      locale,
      locales: supported,
      setGuestLocale: (next) => {
        writeStored(next);
        setGuest(next);
      },
    }),
    [locale, supported],
  );

  if (!catalog || !instance.data) return null;
  return (
    <LocaleContext.Provider value={context}>
      <IntlProvider
        locale={catalog.locale}
        messages={catalog.messages}
        timeZone={instance.data.timezone}
        onError={(error) => {
          if (import.meta.env.DEV) console.warn(error.message);
        }}
      >
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  );
}
